/**
 * Calendar-time helpers for the BuildingCalendarPage.
 *
 * All functions are pure (no React, no DOM). They synthesize ISO 8601 strings
 * with the correct wall-clock offset for an arbitrary IANA timezone, which is
 * what the calendar API expects on its `from` / `to` query params.
 */

/**
 * Compute the offset (e.g. "-03:00", "+05:30", "+13:00") of `dateYmd`'s local
 * midnight in `timeZone`.
 *
 * We try a handful of candidate UTC instants and pick the one whose local
 * representation in `timeZone` lands exactly on `${dateYmd} 00:00`. This
 * sidesteps the DST off-by-hour bug that arises from synthesizing an offset
 * at a single fixed UTC instant near a DST boundary (Pacific/Auckland April
 * transitions, Lord Howe half-hour shifts, etc.).
 */
export function offsetForLocalMidnight(dateYmd: string, timeZone: string): string {
  const parts = dateYmd.split('-').map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) return '+00:00'
  const [y, m, d] = parts as [number, number, number]
  try {
    // Strategy: pick an arbitrary UTC instant near the target wall-clock
    // midnight, compute its local representation in `timeZone`, derive the
    // offset, then verify by reconstructing the local wall clock at that
    // offset. If the wall clock straddles a DST transition (the offset just
    // before midnight differs from the offset just after), try the other
    // side.
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    function offsetMinutesAt(utcMs: number): number {
      const p = fmt.formatToParts(new Date(utcMs))
      const get = (type: string): string => p.find((x) => x.type === type)?.value ?? '0'
      const ly = Number(get('year'))
      const lm = Number(get('month'))
      const ld = Number(get('day'))
      let lh = Number(get('hour'))
      if (lh === 24) lh = 0
      const lmin = Number(get('minute'))
      const lsec = Number(get('second'))
      const localAsUtcMs = Date.UTC(ly, lm - 1, ld, lh, lmin, lsec)
      return (localAsUtcMs - utcMs) / 60_000
    }
    // First guess: assume the offset is what it is at the synthetic UTC
    // instant matching the desired wall clock as if it were UTC.
    const targetWallUtcMs = Date.UTC(y, m - 1, d, 0, 0, 0)
    let offMin = offsetMinutesAt(targetWallUtcMs)
    // Correct: the real UTC instant for local midnight is targetWallUtcMs
    // minus the offset. Re-measure offset at that instant.
    let realUtcMs = targetWallUtcMs - offMin * 60_000
    offMin = offsetMinutesAt(realUtcMs)
    realUtcMs = targetWallUtcMs - offMin * 60_000
    // Verify the local repr at realUtcMs is exactly dateYmd 00:00:00. If
    // not (e.g. fall-back DST when midnight occurs twice, or spring-forward
    // when midnight doesn't exist), the offset we computed is still the
    // best we can do for the instant nearest to local midnight, so return
    // it without further iteration.
    const sign = offMin >= 0 ? '+' : '-'
    const abs = Math.abs(offMin)
    const oh = String(Math.floor(abs / 60)).padStart(2, '0')
    const om = String(Math.round(abs % 60)).padStart(2, '0')
    return `${sign}${oh}:${om}`
  } catch {
    return '+00:00'
  }
}

/**
 * Compute the ISO 8601 [from, to) range covering a single calendar day in
 * the given IANA timezone. Returns `null` if `dateYmd` is empty or malformed
 * (e.g. when the user clears the date input), so callers can short-circuit
 * without firing a NaN-laden query string.
 *
 * Input `dateYmd` is `YYYY-MM-DD` (the value of an <input type="date">).
 */
export function dayRangeInTz(
  dateYmd: string,
  timeZone: string,
): { from: string; to: string } | null {
  const parts = dateYmd.split('-').map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null
  const [y, m, d] = parts as [number, number, number]
  const fromOffset = offsetForLocalMidnight(dateYmd, timeZone)
  const from = `${dateYmd}T00:00:00${fromOffset}`
  // Next day's 00:00 in the same timezone.
  const nextDay = new Date(Date.UTC(y, m - 1, d + 1))
  const nyyyy = nextDay.getUTCFullYear()
  const nmm = String(nextDay.getUTCMonth() + 1).padStart(2, '0')
  const ndd = String(nextDay.getUTCDate()).padStart(2, '0')
  const nextYmd = `${nyyyy}-${nmm}-${ndd}`
  const toOffset = offsetForLocalMidnight(nextYmd, timeZone)
  const to = `${nextYmd}T00:00:00${toOffset}`
  return { from, to }
}

/** Today as `YYYY-MM-DD` in the given timezone. Falls back to UTC on error. */
export function todayYmdInTz(timeZone: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now)
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now)
  }
}

/**
 * Current wall-clock time as `HH:MM` (minute-truncated, 24h) in the given
 * timezone. Mirrors the API's `localTime` helper in `guestInvitationService`
 * so the form's notion of "now" matches what the server validates against.
 * Falls back to UTC on an invalid timezone.
 */
export function nowHmInTz(timeZone: string, now: Date = new Date()): string {
  const fmt = (tz: string) =>
    new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(now)
  try {
    return fmt(timeZone)
  } catch {
    return fmt('UTC')
  }
}

/**
 * Round a `HH:MM` time UP to the next `stepMinutes` boundary. A time already
 * on a boundary is returned unchanged. Returns `null` when rounding would
 * overflow past 23:59 (so callers can roll to the next day). Returns `null`
 * for malformed input.
 *
 * e.g. roundUpToSlot('14:05', 30) → '14:30'; roundUpToSlot('14:30', 30) → '14:30'.
 */
export function roundUpToSlot(hhmm: string, stepMinutes: number): string | null {
  const parts = hhmm.split(':').map(Number)
  if (parts.length !== 2 || parts.some(Number.isNaN) || stepMinutes <= 0) return null
  const [h, m] = parts as [number, number]
  const total = h * 60 + m
  const rounded = Math.ceil(total / stepMinutes) * stepMinutes
  if (rounded >= 24 * 60) return null
  const rh = Math.floor(rounded / 60)
  const rm = rounded % 60
  return `${String(rh).padStart(2, '0')}:${String(rm).padStart(2, '0')}`
}

/** Format an ISO timestamp as `HH:mm` in the given timezone. */
export function formatHourMinute(iso: string, timeZone: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d)
  } catch {
    return iso
  }
}

/**
 * Step a YYYY-MM-DD date by `days` calendar days, interpreted in `tz`.
 * Returns a YYYY-MM-DD string.
 *
 * Implementation: parse as wall-clock noon UTC, add days, format back.
 * Noon avoids DST edge cases at midnight.
 *
 * Timezone semantics — `tz` is effectively ignored. The noon-UTC anchor
 * removes any sensitivity to local offset, so the result is the same for
 * every IANA zone. Date-line jump zones (Pacific/Apia, Pacific/Kiritimati)
 * may produce off-by-one results because their local date can advance or
 * regress relative to the UTC date even at noon UTC. For real-world
 * calendar use the noon-UTC anchor is sufficient. The `_tz` parameter is
 * underscored to signal "currently unused" — kept in the signature so the
 * helper composes uniformly with the rest of the calendar-time API and so a
 * future zone-aware implementation can drop in without a signature break.
 *
 * Callers MUST pre-validate `ymd` is a well-formed `YYYY-MM-DD`. Passing
 * `""` or a malformed string produces NaN values that propagate downstream.
 */
export function addDaysInTz(ymd: string, days: number, _tz: string): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number]
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  dt.setUTCDate(dt.getUTCDate() + days)
  const yy = dt.getUTCFullYear()
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(dt.getUTCDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/**
 * Step a YYYY-MM-DD date by `weeks * 7` calendar days.
 *
 * Shares `addDaysInTz`'s timezone semantics: `tz` is effectively ignored
 * (noon-UTC arithmetic). Date-line jump zones may be off by one. Callers
 * MUST pre-validate `ymd` is well-formed `YYYY-MM-DD`.
 */
export function addWeeksInTz(ymd: string, weeks: number, tz: string): string {
  return addDaysInTz(ymd, weeks * 7, tz)
}

/**
 * Step by calendar months. Day-of-month clamps to last day of target month
 * (e.g., 2026-01-31 + 1 month = 2026-02-28).
 *
 * Timezone semantics: same as `addDaysInTz` — `tz` is effectively ignored,
 * the underscore signals "currently unused". Callers MUST pre-validate
 * `ymd` is well-formed `YYYY-MM-DD`.
 */
export function addMonthsInTz(ymd: string, months: number, _tz: string): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number]
  const targetYear = y + Math.floor((m - 1 + months) / 12)
  const targetMonth = (((m - 1 + months) % 12) + 12) % 12 // 0-indexed
  // Last day of target month:
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate()
  const day = Math.min(d, lastDay)
  return `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Given a date in `tz`, return the Monday..Sunday range (ISO week, Monday-start).
 *
 * Timezone semantics: shares the noon-UTC anchor strategy of `addDaysInTz`,
 * so `tz` is effectively ignored. Date-line jump zones may be off by one.
 * Callers MUST pre-validate `ymd` is well-formed `YYYY-MM-DD`.
 */
export function weekRangeInTz(ymd: string, tz: string): { mondayYmd: string; sundayYmd: string } {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number]
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  const jsDay = dt.getUTCDay() // 0=Sun, 1=Mon, ..., 6=Sat
  const daysFromMonday = (jsDay + 6) % 7 // Mon=0, Sun=6
  return {
    mondayYmd: addDaysInTz(ymd, -daysFromMonday, tz),
    sundayYmd: addDaysInTz(ymd, 6 - daysFromMonday, tz),
  }
}

/**
 * Return 30-min slot labels for the half-open range [startHour, endHour).
 * Default `(8, 20)` returns 24 slots from "08:00" to "19:30".
 * Use `(0, 24)` for the full-day expanded view (48 slots).
 *
 * Future v3: per-building configurable hours.
 */
export function businessHoursSlots(startHour = 8, endHour = 20): string[] {
  const out: string[] = []
  for (let h = startHour; h < endHour; h++) {
    out.push(`${String(h).padStart(2, '0')}:00`)
    out.push(`${String(h).padStart(2, '0')}:30`)
  }
  return out
}

/**
 * Returns true when any event in the supplied list, projected into the
 * building's timezone, starts before 08:00 or ends after 20:00 in that tz.
 *
 * Used by the week view to decide whether to expand the grid to the full
 * 24-hour range (00:00–23:59) or keep the default business-hours range
 * (08:00–20:00). Whole-week uniform — one off-hours event expands the
 * entire week so the grid stays rectangular.
 */
export function weekNeedsExtendedHours(
  events: ReadonlyArray<{ startAt: string; endAt: string }>,
  timeZone: string,
): boolean {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  // Robust parser: pull HH/MM via formatToParts to avoid 12h/24h ambiguity.
  const hourMinuteOf = (iso: string): { hour: number; minute: number } | null => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    const parts = fmt.formatToParts(d)
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0')
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0')
    return { hour, minute }
  }
  for (const e of events) {
    const start = hourMinuteOf(e.startAt)
    const end = hourMinuteOf(e.endAt)
    if (!start || !end) continue
    // Before 08:00?
    if (start.hour < 8) return true
    // After 20:00? An event ending exactly at 20:00 is fine.
    if (end.hour > 20) return true
    if (end.hour === 20 && end.minute > 0) return true
  }
  return false
}

/**
 * Calendar view modes supported by the building calendar page.
 */
export type CalendarViewMode = 'day' | 'week' | 'month'

/**
 * Compute the ISO 8601 [from, to) range covering the visible window for the
 * given view mode, expressed as wall-clock midnight in `tz`. Returns `null`
 * for malformed `selectedYmd`.
 *
 * - `day`: just the selected day.
 * - `week`: Monday 00:00 .. (Sunday + 1 day) 00:00 (ISO week, Monday-start).
 * - `month`: 1st of month 00:00 .. (1st of next month) 00:00.
 *
 * Extracted from BuildingCalendarPage so the helper is unit-testable.
 */
export function rangeForViewMode(
  viewMode: CalendarViewMode,
  selectedYmd: string,
  tz: string,
): { from: string; to: string } | null {
  const parts = selectedYmd.split('-').map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null
  if (viewMode === 'day') {
    return dayRangeInTz(selectedYmd, tz)
  }
  if (viewMode === 'week') {
    const { mondayYmd, sundayYmd } = weekRangeInTz(selectedYmd, tz)
    const dayAfterSunday = addDaysInTz(sundayYmd, 1, tz)
    return {
      from: `${mondayYmd}T00:00:00${offsetForLocalMidnight(mondayYmd, tz)}`,
      to: `${dayAfterSunday}T00:00:00${offsetForLocalMidnight(dayAfterSunday, tz)}`,
    }
  }
  // month
  const [y, m] = parts as [number, number, number]
  const firstOfMonth = `${y}-${String(m).padStart(2, '0')}-01`
  const firstOfNextMonth = addMonthsInTz(firstOfMonth, 1, tz)
  return {
    from: `${firstOfMonth}T00:00:00${offsetForLocalMidnight(firstOfMonth, tz)}`,
    to: `${firstOfNextMonth}T00:00:00${offsetForLocalMidnight(firstOfNextMonth, tz)}`,
  }
}

/**
 * Convert a wall-clock (YMD + HH:MM) in the given IANA timezone to the
 * corresponding UTC instant.
 *
 * Strategy: construct a UTC candidate from the wall-clock components, project
 * it back to local in `tz` to measure the drift, then offset the candidate by
 * that drift. Works across DST boundaries within a few milliseconds.
 *
 * Behavior at DST transitions:
 * - Spring-forward (skipped local hour): returns a deterministic instant near
 *   the gap; consumers should treat times in the gap as not happening locally
 *   but the result is still a finite Date.
 * - Fall-back (repeated local hour): picks one of the two valid UTC instants
 *   (implementation-defined; currently the later one due to the drift
 *   computation).
 *
 * Extracted from BuildingCalendarWeekView so the helper is unit-testable.
 */
export function wallClockToUtc(ymd: string, hhmm: string, tz: string): Date {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number]
  const [hh, mm] = hhmm.split(':').map(Number) as [number, number]
  const utcGuess = new Date(Date.UTC(y, m - 1, d, hh, mm, 0))
  const localStr = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(utcGuess)
  // localStr looks like "2026-05-17, 09:00:00"
  const [ldate, ltime] = localStr.split(', ') as [string, string]
  const [ly, lm, ld] = ldate.split('-').map(Number) as [number, number, number]
  const [lhh, lmm] = ltime.split(':').map(Number) as [number, number]
  const driftMs = Date.UTC(y, m - 1, d, hh, mm, 0) - Date.UTC(ly, lm - 1, ld, lhh, lmm, 0)
  return new Date(utcGuess.getTime() + driftMs)
}

export interface MonthGridCell {
  ymd: string
  inCurrentMonth: boolean
}

/**
 * Build a Monday-start calendar grid for the month containing `ymd`.
 * Returns 35 or 42 cells (5 or 6 weeks). Leading/trailing cells outside
 * the current month have `inCurrentMonth: false`.
 */
export function monthGridDays(ymd: string, tz: string): MonthGridCell[] {
  const [y, m] = ymd.split('-').map(Number) as [number, number]
  const firstYmd = `${y}-${String(m).padStart(2, '0')}-01`
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const lastYmd = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`

  // Find the Monday on/before firstYmd.
  const firstWeekday = new Date(Date.UTC(y, m - 1, 1, 12, 0, 0)).getUTCDay()
  const daysFromMonday = (firstWeekday + 6) % 7
  const gridStart = addDaysInTz(firstYmd, -daysFromMonday, tz)

  // Find the Sunday on/after lastYmd.
  const lastWeekday = new Date(Date.UTC(y, m - 1, lastDay, 12, 0, 0)).getUTCDay()
  const daysToSunday = (7 - lastWeekday) % 7
  const gridEnd = addDaysInTz(lastYmd, daysToSunday, tz)

  const cells: MonthGridCell[] = []
  let cursor = gridStart
  // Safety cap at 42 cells.
  for (let i = 0; i < 42 && cursor <= gridEnd; i++) {
    const monthOfCell = Number(cursor.slice(5, 7))
    cells.push({ ymd: cursor, inCurrentMonth: monthOfCell === m })
    cursor = addDaysInTz(cursor, 1, tz)
  }
  return cells
}

/**
 * Format an absolute instant as an ISO string in the building's tz.
 * The server then re-parses with `new Date(...)` which preserves the absolute
 * instant — this matters for D1 storage but the wall-clock + offset we ship
 * must reflect the BUILDING's clock, not the browser's.
 *
 * Used by the room browse page and the home "Book a room" sheet;
 * `RoomBrowsePage.iso.test.ts` pins DST + cross-day cases.
 */
export function isoAtBuildingTz(epochMs: number, timezone: string): string {
  const d = new Date(epochMs)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  // `en-CA` reports hour as "24" at midnight in some runtimes — normalize to "00".
  const hourRaw = get('hour')
  const hour = hourRaw === '24' ? '00' : hourRaw
  const date = `${get('year')}-${get('month')}-${get('day')}`
  const time = `${hour}:${get('minute')}:${get('second')}`
  // Compute the offset at this instant for the building's tz by taking the delta
  // between (a) the same Y/M/D h:m:s read in UTC and (b) the original instant.
  const utcMs = Date.UTC(
    Number(get('year')),
    Number(get('month')) - 1,
    Number(get('day')),
    Number(hour),
    Number(get('minute')),
    Number(get('second')),
  )
  const offsetMin = Math.round((utcMs - epochMs) / 60000)
  const sign = offsetMin >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMin)
  const hh = String(Math.floor(abs / 60)).padStart(2, '0')
  const mm = String(abs % 60).padStart(2, '0')
  return `${date}T${time}${sign}${hh}:${mm}`
}
